import type { DeploymentRecord } from '@dev-console/types';

export const DEPLOYMENT_TIMELINE_STEPS = ['started', 'build', 'deploy', 'complete'] as const;
export type DeploymentTimelineStepId = (typeof DEPLOYMENT_TIMELINE_STEPS)[number];

export type DeploymentTimelineStepState = 'pending' | 'active' | 'done' | 'failed' | 'skipped';

export type DeploymentTimelineStep = {
  id: DeploymentTimelineStepId;
  label: string;
  state: DeploymentTimelineStepState;
};

const STEP_LABELS: Record<DeploymentTimelineStepId, string> = {
  started: 'started',
  build: 'build',
  deploy: 'deploy',
  complete: 'complete',
};

type TimelineSource = Pick<DeploymentRecord, 'status' | 'phase' | 'mode'>;

/** Connector visual between two steps (or half-segment beside a circle). */
type ConnectorVisual = 'pending' | 'progress' | 'done' | 'failed' | 'skipped' | 'none';

function states(values: Record<DeploymentTimelineStepId, DeploymentTimelineStepState>): DeploymentTimelineStep[] {
  return DEPLOYMENT_TIMELINE_STEPS.map((id) => ({
    id,
    label: STEP_LABELS[id],
    state: values[id],
  }));
}

/** Derive stepper states from a deployment history record. */
export function getDeploymentTimelineSteps(record: TimelineSource): DeploymentTimelineStep[] {
  const { status, phase } = record;

  if (status === 'success') {
    return states({
      started: 'done',
      build: 'done',
      deploy: 'done',
      complete: 'done',
    });
  }

  if (status === 'skipped') {
    return states({
      started: 'done',
      build: 'skipped',
      deploy: 'skipped',
      complete: 'skipped',
    });
  }

  if (status === 'in_progress') {
    if (phase === 'deploy') {
      return states({
        started: 'done',
        build: 'done',
        deploy: 'active',
        complete: 'pending',
      });
    }
    // phase=build, or unknown — treat as still building
    return states({
      started: 'done',
      build: 'active',
      deploy: 'pending',
      complete: 'pending',
    });
  }

  if (status === 'failure' || status === 'cancelled') {
    if (phase === 'build') {
      return states({
        started: 'done',
        build: 'failed',
        deploy: 'pending',
        complete: 'pending',
      });
    }
    // Terminal rows usually clear phase; assume deploy was the failing step.
    return states({
      started: 'done',
      build: 'done',
      deploy: 'failed',
      complete: 'pending',
    });
  }

  return states({
    started: 'pending',
    build: 'pending',
    deploy: 'pending',
    complete: 'pending',
  });
}

const CIRCLE_CLASS: Record<DeploymentTimelineStepState, string> = {
  pending: 'border border-neutral-300 bg-white',
  active: 'bg-blue-600 shadow-[0_0_0_3px_rgba(37,99,235,0.18)] animate-pulse',
  done: 'bg-green-500',
  failed: 'bg-red-500',
  skipped: 'bg-amber-400',
};

const LABEL_CLASS: Record<DeploymentTimelineStepState, string> = {
  pending: 'text-neutral-400',
  active: 'text-blue-700 font-medium',
  done: 'text-neutral-600',
  failed: 'text-red-700 font-medium',
  skipped: 'text-amber-700',
};

const CONNECTOR_CLASS: Record<Exclude<ConnectorVisual, 'none'>, string> = {
  pending: 'h-1 bg-neutral-200',
  progress: 'h-1 bg-blue-300',
  done: 'h-1 bg-green-400',
  failed: 'h-1 bg-red-400',
  skipped: 'h-1 bg-amber-300',
};

function connectorIntoStep(
  previous: DeploymentTimelineStepState | null,
  current: DeploymentTimelineStepState
): ConnectorVisual {
  if (previous == null) return 'none';
  if (previous === 'failed') return 'failed';
  if (previous === 'skipped') return 'skipped';
  if (previous === 'done' && current === 'active') return 'progress';
  if (previous === 'done') return 'done';
  return 'pending';
}

function connectorOutOfStep(
  current: DeploymentTimelineStepState,
  next: DeploymentTimelineStepState | null
): ConnectorVisual {
  if (next == null) return 'none';
  if (current === 'failed') return 'failed';
  if (current === 'skipped') return 'skipped';
  if (current === 'done' && next === 'active') return 'progress';
  if (current === 'done') return 'done';
  return 'pending';
}

interface DeploymentTimelineProps {
  record: TimelineSource;
  className?: string;
}

export function DeploymentTimeline({ record, className = '' }: DeploymentTimelineProps) {
  const steps = getDeploymentTimelineSteps(record);

  return (
    <ol className={`mx-auto grid w-full min-w-0 grid-cols-4 items-start ${className}`} aria-label='Deployment progress'>
      {steps.map((step, index) => {
        const previous = index > 0 ? steps[index - 1] : null;
        const next = index < steps.length - 1 ? steps[index + 1] : null;
        const left = connectorIntoStep(previous?.state ?? null, step.state);
        const right = connectorOutOfStep(step.state, next?.state ?? null);

        return (
          <li key={step.id} className='flex min-w-0 flex-col items-center'>
            <div className='flex w-full items-center'>
              <div
                className={`min-w-0 flex-1 ${left === 'none' ? 'h-1 bg-transparent' : CONNECTOR_CLASS[left]}`}
                aria-hidden='true'
              />
              <span
                className={`size-3.5 shrink-0 rounded-full ${CIRCLE_CLASS[step.state]}`}
                title={`${step.label}: ${step.state}`}
                aria-label={`${step.label}: ${step.state}`}
              />
              <div
                className={`min-w-0 flex-1 ${right === 'none' ? 'h-1 bg-transparent' : CONNECTOR_CLASS[right]}`}
                aria-hidden='true'
              />
            </div>
            <span
              className={`mt-1.5 w-full break-words px-0.5 text-center text-[10px] leading-tight sm:text-[11px] ${
                LABEL_CLASS[step.state]
              }`}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
